if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        // If this product currently has a saved engraving, the customer
        // must explicitly re-review and confirm it before Add to Cart
        // actually submits - every click, not just the first (see
        // gift-options.js's setupEngravingReview). Products without
        // engraving (or without the gift-options block at all) skip
        // straight to submitAddToCart as before.
        const engravingOptions = this.findEngravingOptionsElement();
        if (engravingOptions && typeof engravingOptions.engravingNeedsReview === 'function' && engravingOptions.engravingNeedsReview()) {
          engravingOptions.openEngravingReviewModal(() => this.submitAddToCart(evt));
          return;
        }

        this.submitAddToCart(evt);
      }

      findEngravingOptionsElement() {
        if (this.form && this.form.id) {
          const hiddenInput = document.querySelector(`engraving-options input[form="${this.form.id}"]`);
          if (hiddenInput) return hiddenInput.closest('engraving-options');
        }
        return document.querySelector('engraving-options');
      }

      findGiftWrappingOptionsElement() {
        if (this.form && this.form.id) {
          const hiddenInput = document.querySelector(`gift-wrapping-options input[form="${this.form.id}"]`);
          if (hiddenInput) return hiddenInput.closest('gift-wrapping-options');
        }
        return document.querySelector('gift-wrapping-options');
      }

      submitAddToCart(evt) {
        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);

        // Plumbing field only (see gift_options.liquid) - tells us which gift
        // wrap variant to add as its own cart line, paired to the main line
        // via the `_Gift Wrap Id` property that's already part of formData.
        // Strip it out before submitting so it never hits /cart/add.js.
        const giftWrapVariantId = formData.get('_giftwrap_variant_id');
        formData.delete('_giftwrap_variant_id');
        const giftWrapPairId = formData.get('properties[_Gift Wrap Id]');

        const engravingFeeVariantId = formData.get('_engraving_fee_variant_id');
        formData.delete('_engraving_fee_variant_id');
        const engravingId = formData.get('properties[_Engraving Id]');

        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        
        const algoliaQueryID = sessionStorage.getItem('algolia_queryID');
        if (algoliaQueryID) {
          formData.append('properties[_algolia_queryID]', algoliaQueryID);
        }
        config.body = formData;

        const variantId = formData.get('id');
        const quantity = parseInt(formData.get('quantity')) || 1;
        const linesUpdateDeferred = this.createCartLinesUpdateEvent(variantId, quantity);

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: variantId,
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);
              this.dispatchCartErrorEvent(response.description || response.message, 'INVALID');
              linesUpdateDeferred?.reject(new Error(response.description || response.message));

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              this.resolveCartLinesUpdate(linesUpdateDeferred);
              window.location = window.routes.cart_url;
              return;
            }

            this.resolveCartLinesUpdate(linesUpdateDeferred);

            const finishAdd = (finalResponse) => {
              // --- Algolia Add To Cart Tracking ---
              try {
                const addedItem = finalResponse.items ? (finalResponse.items.find((i) => i.variant_id == variantId || i.id == variantId) || finalResponse.items[0]) : finalResponse;
                if (addedItem && !this.error) {
                  const price = addedItem.price / 100;
                  const productContainer = this.closest('.card-wrapper, .card, .product, .shopify-section') || document;
                  
                  // Query all potential compare price elements in the container
                  const compareEls = productContainer.querySelectorAll('.price__sale s.price-item--regular, s.price-item--regular');
                  let discount = 0;
                  
                  // Iterate through them until we find one with actual text content to avoid hidden/empty elements returning NaN
                  for (let i = 0; i < compareEls.length; i++) {
                    const text = (compareEls[i].textContent || compareEls[i].innerText || '').replace(/[^0-9.]/g, '');
                    if (text.length > 0) {
                      const comparePrice = parseFloat(text);
                      if (comparePrice > price) {
                        discount = comparePrice - price;
                        console.log('Algolia PDP Tracker - Extracted Compare Price:', comparePrice, 'Calculated Discount:', discount);
                        break;
                      }
                    }
                  }
                  const props = { price: price, quantity: quantity, discount_value: discount };
                  if (sessionStorage.getItem('algolia_queryID')) {
                    if(typeof sendConvertedObjectIDsAfterSearch === 'function') sendConvertedObjectIDsAfterSearch(variantId, props);
                    if(typeof sendAddedToCartObjectIDsAfterSearch === 'function') sendAddedToCartObjectIDsAfterSearch(variantId, props);
                  } else {
                    if(typeof sendConvertedObjectIDs === 'function') sendConvertedObjectIDs(variantId);
                    if(typeof sendAddedToCartObjectIDs === 'function') sendAddedToCartObjectIDs(variantId, props);
                  }
                }
              } catch (err) { console.error('Algolia Tracking Error', err); }
              // ------------------------------------

              const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
              if (!this.error)
                publish(PUB_SUB_EVENTS.cartUpdate, {
                  source: 'product-form',
                  productVariantId: variantId,
                  cartData: finalResponse,
                }).then(() => {
                  CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
                });
              this.error = false;
              const quickAddModal = this.closest('quick-add-modal');
              if (quickAddModal) {
                document.body.addEventListener(
                  'modalClosed',
                  () => {
                    setTimeout(() => {
                      CartPerformance.measure("add:paint-updated-sections", () => {
                        this.cart.renderContents(finalResponse);
                      });
                    });
                  },
                  { once: true }
                );
                quickAddModal.hide(true);
              } else {
                CartPerformance.measure("add:paint-updated-sections", () => {
                  this.cart.renderContents(finalResponse);
                });
              }
            };

            let addPromise = Promise.resolve(response);

            let childrenToAdd = [];
            if (giftWrapVariantId) {
              childrenToAdd.push({
                id: parseInt(giftWrapVariantId, 10),
                quantity: quantity || 1,
                properties: {
                  '_Gift Wrap Id': giftWrapPairId,
                  '_Gift Wrap Item': 'true',
                },
              });
            }
            
            if (engravingFeeVariantId) {
              childrenToAdd.push({
                id: parseInt(engravingFeeVariantId, 10),
                quantity: quantity || 1,
                properties: {
                  '_Linked Engraving Id': engravingId,
                  '_type': 'engraving-fee',
                },
              });
            }

            if (childrenToAdd.length > 0) {
              addPromise = addPromise.then((currentResponse) => {
                const addedVariantId = parseInt(variantId, 10);
                const parentItem = currentResponse.items
                  ? currentResponse.items.find((item) => item.variant_id === addedVariantId || item.id === addedVariantId) || currentResponse.items[0]
                  : currentResponse;
                const parentLineKey = parentItem ? parentItem.key : undefined;

                childrenToAdd.forEach(item => {
                  item.parent_line_key = parentLineKey || undefined;
                });

                const sectionsToRender = this.cart ? this.cart.getSectionsToRender() : [];
                const body = JSON.stringify({
                  items: childrenToAdd,
                  sections: sectionsToRender.map((section) => section.id),
                  sections_url: window.location.pathname,
                });

                return fetch(`${routes.cart_add_url}`, { ...fetchConfig(), body })
                  .then((r) => r.json())
                  .then((childrenResponse) => {
                    if (childrenResponse && childrenResponse.status) {
                      throw new Error(childrenResponse.description || childrenResponse.message || 'Failed to add child items');
                    }
                    return childrenResponse && childrenResponse.sections
                      ? { ...currentResponse, sections: childrenResponse.sections }
                      : currentResponse;
                  })
                  .catch((err) => {
                    console.error('[AddChildren] Network error adding child items:', err);
                    throw err;
                  });
              });
            }

            addPromise.then((finalResponse) => finishAdd(finalResponse));
          })
          .catch((e) => {
            console.error(e);
            this.dispatchCartErrorEvent(e.message || 'Network error', 'SERVICE_UNAVAILABLE');
            linesUpdateDeferred?.reject(e);
          })
          .finally(() => {
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) {
              this.submitButton.removeAttribute('aria-disabled');
              const checkmark = this.submitButton.querySelector('.loading__checkmark');
              const spinner = this.querySelector('.loading__spinner');
              if (checkmark && spinner) {
                spinner.classList.add('hidden');
                checkmark.classList.remove('hidden');
                setTimeout(() => {
                  checkmark.classList.add('hidden');
                  this.submitButton.classList.remove('loading');
                }, 1000);
              } else {
                this.submitButton.classList.remove('loading');
                if (spinner) spinner.classList.add('hidden');
              }
            } else {
              this.submitButton.classList.remove('loading');
              const spinner = this.querySelector('.loading__spinner');
              if (spinner) spinner.classList.add('hidden');
            }

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      createCartLinesUpdateEvent(variantId, quantity) {
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return null;

        const deferred = CartLinesUpdateEvent.createPromise();
        this.dispatchEvent(
          new CartLinesUpdateEvent({
            action: 'add',
            context: 'product',
            lines: [{ merchandiseId: variantId, quantity }],
            promise: deferred.promise,
          })
        );
        return deferred;
      }

      resolveCartLinesUpdate(deferred) {
        if (!deferred) return;
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return;

        const pendingCartDataPromise = typeof CartItems !== 'undefined'
          ? CartItems.fetchCartData()
          : fetch(`${routes.cart_url}.json`).then((response) => response.json());

        pendingCartDataPromise
          .then((cart) => {
            if (!cart?.currency) return deferred.reject(new Error('Missing currency in cart response'));
            deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart) });
          })
          .catch((e) => deferred.reject(e));
      }

      // Adds the selected gift wrap product as its own cart line, carrying
      // the same pairId as the main line's `_Gift Wrap Id` property (and a
      // `_Gift Wrap Item` marker so cart.js knows this is the wrap side of
      // the pair) so the two can be cascade-removed together later. Quantity
      // matches the main line's own quantity (it's sold one-per-unit), not
      // hardcoded to 1, so a 5-quantity add-to-cart charges for 5 wraps too.
      addLinkedGiftWrapItem(variantId, pairId, quantity, parentLineKey) {
        if (!variantId) return Promise.resolve(null);

        const sectionsToRender = this.cart.getSectionsToRender();
        const body = JSON.stringify({
          items: [
            {
              id: parseInt(variantId, 10),
              quantity: quantity || 1,
              parent_line_key: parentLineKey || undefined,
              properties: {
                '_Gift Wrap Id': pairId,
                '_Gift Wrap Item': 'true',
              },
            },
          ],
          sections: sectionsToRender.map((section) => section.id),
          sections_url: window.location.pathname,
        });

        return fetch(`${routes.cart_add_url}`, { ...fetchConfig(), body })
          .then((r) => r.json())
          .catch((err) => {
            console.error('[GiftWrap] Network error adding gift wrap item:', err);
            return null;
          });
      }

      addLinkedEngravingFeeItem(variantId, engravingId, quantity, parentLineKey) {
        if (!variantId) return Promise.resolve(null);

        const sectionsToRender = this.cart.getSectionsToRender();
        const body = JSON.stringify({
          items: [
            {
              id: parseInt(variantId, 10),
              quantity: quantity || 1,
              parent_line_key: parentLineKey || undefined,
              properties: {
                '_Linked Engraving Id': engravingId,
                '_type': 'engraving-fee',
              },
            },
          ],
          sections: sectionsToRender.map((section) => section.id),
          sections_url: window.location.pathname,
        });

        return fetch(`${routes.cart_add_url}`, { ...fetchConfig(), body })
          .then((r) => r.json())
          .catch((err) => {
            console.error('[EngravingFee] Network error adding engraving fee item:', err);
            return null;
          });
      }

      dispatchCartErrorEvent(message, code) {
        const { CartErrorEvent } = window.StandardEvents || {};
        if (!CartErrorEvent) return;
        this.dispatchEvent(new CartErrorEvent({ error: message, code }));
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
